// YtScansPage.js — Dark Pattern Review Tool
// Videos tab: displays all YT scans for the org as thumbnail cards.
// Admin-only (gated by REACT_APP_ADMIN_ORG_ID in ClientHeader).

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchYtScans, getOrgId } from "../api";
import ClientHeader from "./ClientHeader";

const ADMIN_ORG_ID = process.env.REACT_APP_ADMIN_ORG_ID;

const formatDate = (ts) => {
    if (!ts) return "—";
    try {
        const d = new Date(ts);
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return ts;
    }
};

const StatusBadge = ({ status }) => {
    const map = {
        completed: { bg: "#E6F4EA", color: "#137333", label: "Completed" },
        processing: { bg: "#FEF7E0", color: "#B06000", label: "Processing" },
        failed: { bg: "#FDECEA", color: "#B3261E", label: "Failed" },
    };
    const s = map[status] || map.processing;
    return (
        <span style={{
            background: s.bg,
            color: s.color,
            fontSize: 11,
            fontWeight: 600,
            padding: "2px 10px",
            borderRadius: 20,
            display: "inline-block",
        }}>
            {s.label}
        </span>
    );
};

const YtScansPage = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const [scans, setScans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const isAdmin = getOrgId() === ADMIN_ORG_ID;

    useEffect(() => {
        if (!isAdmin) return;
        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const data = await fetchYtScans();
                setScans(data || []);
            } catch (err) {
                setError(err.message || "Failed to load YT scans.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [isAdmin]);

    if (!isAdmin) {
        return (
            <div className="sessions-page-container">
                <ClientHeader user={user} onLogout={onLogout} />
                <div className="cr-page">
                    <div className="cr-empty">Access restricted to administrators.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="sessions-page-container">
            <ClientHeader user={user} onLogout={onLogout} />
            <div className="cr-page">
                <div className="cr-page-title-row" style={{ marginBottom: 28 }}>
                    <h1 className="cr-page-title">YouTube Video Scans</h1>

                </div>

                {error && <div className="cr-alert cr-alert-error" style={{ marginBottom: 16 }}>{error}</div>}

                {loading ? (
                    <div className="cr-loading">Loading scans…</div>
                ) : scans.length === 0 ? (
                    <div className="cr-empty">No YouTube scans found for your organisation.</div>
                ) : (
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: 20,
                    }}>
                        {scans.map((scan) => (
                            <div
                                key={scan.scan_id}
                                onClick={() => navigate(`/yt-scan/${scan.scan_id}`)}
                                style={{
                                    background: "var(--white)",
                                    border: "1px solid var(--border)",
                                    borderRadius: 14,
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    transition: "box-shadow 0.2s ease, transform 0.15s ease",
                                    boxShadow: "0 2px 8px rgba(16,24,40,0.06)",
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.boxShadow = "0 6px 24px rgba(145,128,253,0.18)";
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(16,24,40,0.06)";
                                    e.currentTarget.style.transform = "translateY(0)";
                                }}
                            >
                                {/* Thumbnail */}
                                <div style={{ position: "relative", height: 160, background: "#F1F3F4", overflow: "hidden" }}>
                                    { false ? ( null ) : (
                                        <div style={{
                                            width: "100%", height: "100%",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            background: "linear-gradient(135deg, #f1f3f4 0%, #e8eaed 100%)",
                                        }}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" fill="#DADCE0" />
                                                <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#9EA3AF" />
                                            </svg>
                                        </div>
                                    )}
                                    {scan.pattern_count > 0 && (
                                        <div style={{
                                            position: "absolute", top: 8, right: 8,
                                            background: "#EB4335", color: "#fff",
                                            fontSize: 11, fontWeight: 700,
                                            padding: "3px 9px", borderRadius: 20,
                                        }}>
                                            {scan.pattern_count} DP{scan.pattern_count !== 1 ? "s" : ""}
                                        </div>
                                    )}
                                </div>

                                {/* Card body */}
                                <div style={{ padding: "14px 16px 16px" }}>
                                    <div style={{ marginBottom: 8 }}>
                                        <div style={{
                                            fontSize: 14, fontWeight: 600,
                                            color: "var(--text-dark)", lineHeight: 1.4,
                                            display: "-webkit-box", WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical", overflow: "hidden",
                                        }}>
                                            {scan.video_title || scan.video_id}
                                        </div>
                                        {scan.channel_title && (
                                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                                                {scan.channel_title}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                        <StatusBadge status={scan.status} />
                                        {scan.confidence_score != null && (
                                            <span style={{
                                                fontSize: 11, color: "var(--text-mid)",
                                                background: "var(--bg)", padding: "2px 8px",
                                                borderRadius: 4, border: "1px solid var(--border)",
                                            }}>
                                                {Math.round(scan.confidence_score * 100)}% conf.
                                            </span>
                                        )}
                                        <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto" }}>
                                            {formatDate(scan.created_at)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default YtScansPage;